import { MarketIndex, MarketTrades, PerpSymbol } from 'api/v1/market'
import { PerpOrderRecord, PerpPositionRecord } from 'api/v1/perp'
import { TradingFeeRates } from 'api/v1/spot'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { useMarketIndexes, useMarketTrades } from 'store/market/hooks'
import {
  OrderDirection,
  OrderModeDetails,
  OrderModeNames,
} from 'store/spot/types'
import { L2ChainId, SymbolId, SymbolName, TokenSymbol } from 'types'
import {
  PerpClosedOrders,
  PerpCreateOrderOption,
  PerpLastInputAmountType,
  PerpOrderTabType,
  PerpPositionClearingsType,
} from './types'
import { DEFAULT_LEVERAGE } from 'constants/trade'

export function usePerpSymbols() {
  const market = useSelector<RootState, MarketTrades>(
    (state) => state.market.trades,
  )
  return market.contractsSymbols
}
export function usePerpSymbolById(symbolId: SymbolId) {
  const market = useSelector<RootState, MarketTrades>(
    (state) => state.market.trades,
  )
  return useMemo(() => {
    return market.contractsSymbols.find((v) => v.id === symbolId)
  }, [symbolId, market])
}
export function useCurrentPerpSymbol(): Partial<PerpSymbol> {
  const currentPerpSymbol = useSelector<RootState, SymbolId>(
    (state) => state.perp.currentPerpSymbol,
  )
  const market = useSelector<RootState, MarketTrades>(
    (state) => state.market.trades,
  )
  return useMemo(() => {
    return market.contractsSymbols.find((v) => v.id === currentPerpSymbol) ?? {}
  }, [currentPerpSymbol, market])
}
export function usePerpCurrencies(l2ChainId?: L2ChainId) {
  const trades = useMarketTrades()
  return useMemo(() => {
    const currencies = trades.currencies.filter((v) =>
      trades.contractsCurrencies.includes(v.name),
    )
    if (l2ChainId === undefined) {
      return currencies
    } else {
      return currencies.filter(
        (v) => v.chains && v.chains[l2ChainId] !== undefined,
      )
    }
  }, [trades, l2ChainId])
}
export function usePerpOpenOrders() {
  return useSelector<RootState, PerpOrderRecord[]>(
    (state) => state.perp.openOrders,
  )
}
export function usePerpBadgeOrder() {
  return useSelector<RootState, boolean>((state) => state.perp.badgeOrder)
}
export function usePerpBadgeTrade() {
  return useSelector<RootState, boolean>((state) => state.perp.badgeTrade)
}
export function usePerpFeeRates() {
  return useSelector<RootState, TradingFeeRates | undefined>(
    (state) => state.perp.feeRates,
  )
}
export function usePerpPositions() {
  const positions = useSelector<RootState, PerpPositionRecord[]>(
    (state) => state.perp.positions,
  )
  return useMemo(() => {
    return positions.filter((v) => Number(v.quantity) > 0)
  }, [positions])
}
export function useFullPositions() {
  return useSelector<RootState, PerpPositionRecord[]>(
    (state) => state.perp.positions,
  )
}

export function usePerpPositionsBySymbolId(symbolId: SymbolId) {
  const positions = usePerpPositions()
  return useMemo(() => {
    return positions.filter((item) => item.symbolId === symbolId)
  }, [positions, symbolId])
}

export function usePerpCreateOrderOption() {
  return useSelector<RootState, PerpCreateOrderOption>(
    (state) => state.perp.perpCreateOrderOption,
  )
}
export function useClosePositionSymbols() {
  return useSelector<RootState, string[]>(
    (state) => state.perp.closePositionSymbols,
  )
}
export function usePerpSelectedOrderMode() {
  const currentOrderMode = useSelector<RootState, OrderModeNames>(
    (state) => state.perp.orderMode,
  )
  return useMemo(() => {
    return (
      OrderModeDetails.find((v) => v.name === currentOrderMode) ??
      OrderModeDetails[0]
    )
  }, [currentOrderMode])
}
export function usePerpLastInputAmountType() {
  return useSelector<RootState, PerpLastInputAmountType>(
    (state) => state.perp.perpLastInputAmountType,
  )
}
export function usePerpOrderDirection() {
  return useSelector<RootState, OrderDirection>(
    (state) => state.perp.perpOrderDirection,
  )
}
export function useCurrentPerpOrderTab() {
  return useSelector<RootState, PerpOrderTabType>(
    (state) => state.perp.orderTab,
  )
}
export function useCancelPerpPendingIds() {
  return useSelector<RootState, number[]>(
    (state) => state.perp.cancelPerpPendingIds,
  )
}
export function usePerpClosedOrders() {
  return useSelector<RootState, PerpClosedOrders>(
    (state) => state.perp.closedOrders,
  )
}
export function usePerpPositionClearings() {
  return useSelector<RootState, PerpPositionClearingsType>(
    (state) => state.perp.positionClearings,
  )
}
export function useFundingRate(baseCurrency: TokenSymbol): MarketIndex {
  const indexes = useMarketIndexes()
  return useMemo(() => {
    const index = indexes[baseCurrency + '_FR8H']
    return index ?? [0, 0]
  }, [indexes, baseCurrency])
}
export function useNextFundingTime() {
  const indexes = useMarketIndexes()
  return indexes.nextFundingTime[0]
}
export function useCloseAllPerpState() {
  return useSelector<RootState, boolean>(
    (state) => state.perp.closeAllPerpState,
  )
}
export function useCancelAllPerpState() {
  return useSelector<RootState, boolean>(
    (state) => state.perp.cancelAllPerpState,
  )
}
export function usePerpPostOnly() {
  return useSelector<RootState, boolean>((state) => state.perp.postOnly)
}
export function usePositionLeverage(symbolName: SymbolName) {
  const positions = useFullPositions()
  return useMemo(() => {
    const { leverage } =
      positions.find(
        (item) => item.symbol.toUpperCase() === symbolName.toUpperCase(),
      ) ?? {}

    return leverage ?? DEFAULT_LEVERAGE
  }, [positions, symbolName])
}
